import React from 'react'
import { observer } from 'mobx-react-lite'
import { endOfMonth } from 'date-fns/endOfMonth'
import { startOfMonth } from 'date-fns/startOfMonth'

import Stack from '@mui/material/Stack'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'

import { ChevronRight } from '@mxenabled/mx-icons'

import { InstitutionLogo } from '@mxenabled/mxui'

import {
  CurrencyInput,
  Dialog,
  ToggleButtonListItem,
  useAccountStore,
  useGlobalCopyStore,
} from '../../../common'
import { AccountNotificationTypes, DetailedNotificationProfile } from '../../../core'
import { formatCurrency, formatOrdinal } from '../../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../../core/utils/Localization'

interface AccountProfileListItemProps {
  onProfileUpdated: (profile: DetailedNotificationProfile) => void
  profile: DetailedNotificationProfile
}

export const AccountProfileListItem = observer(
  ({ onProfileUpdated, profile }: AccountProfileListItemProps) => {
    const { common: commonCopy, recurring: recurringCopy, settings: copy } = useGlobalCopyStore()
    const { updateAccount } = useAccountStore()

    const isPaymentReminder =
      profile.notification_type === AccountNotificationTypes.DebtPaymentReminder

    const [isOpen, setIsOpen] = React.useState(false)
    const [amount, setAmount] = React.useState<number>(profile.threshold || 0)
    const [paymentDay, setPaymentDay] = React.useState<number>(
      profile.account?.day_payment_is_due || 0,
    )

    const accountName = profile.account?.name || profile.account?.feed_name || ''
    const accountPaymentDate = profile.account?.day_payment_is_due
      ? (buildDynamicCopy(
          copy.notification_debt_payment_date,
          formatOrdinal(profile.account?.day_payment_is_due),
        ) as string)
      : copy.notification_debt_payment_zero_state

    const secondaryText = isPaymentReminder
      ? accountPaymentDate
      : formatCurrency(profile.threshold ?? 0, '0,0')

    const handleUpdateProfile = async () => {
      if (isPaymentReminder && profile.account) {
        await updateAccount({ ...profile.account, day_payment_is_due: paymentDay })
      } else {
        onProfileUpdated({ ...profile, threshold: amount })
      }
    }

    return (
      <Stack>
        <ToggleButtonListItem
          avatar={
            profile.account?.institution_guid ? (
              <InstitutionLogo
                alt={profile.label}
                institutionGuid={profile.account?.institution_guid}
              />
            ) : undefined
          }
          endIcon={<ChevronRight />}
          isChecked={profile.is_enabled}
          isDisabled={!profile.is_enabled}
          isPrimaryTextBold={true}
          onClick={() => setIsOpen(true)}
          onToggle={() =>
            onProfileUpdated({
              ...profile,
              is_enabled: !profile.is_enabled,
            })
          }
          primaryText={accountName}
          secondaryText={!profile.is_enabled ? copy.notification_disabled : secondaryText}
        />

        <Dialog
          copy={{
            title: isPaymentReminder
              ? recurringCopy.day_of_the_month
              : copy.notification_threshold_label,
            close_aria: commonCopy.close_aria,
          }}
          disablePrimaryButton={isPaymentReminder && paymentDay === 0}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onPrimaryAction={handleUpdateProfile}
          primaryText={commonCopy.save_button}
        >
          {isPaymentReminder ? (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                maxDate={endOfMonth(new Date())}
                minDate={startOfMonth(new Date())}
                onChange={(date: Date) => {
                  setPaymentDay(date.getDate())
                }}
                slots={{
                  // Hides the arrows
                  leftArrowIcon: () => null,
                  rightArrowIcon: () => null,
                }}
                sx={{
                  // TODO: Needs to be updated in MXUI
                  '& .MuiPickersDay-today': {
                    color: 'primary.main',
                  },
                }}
                views={['day']}
              />
            </LocalizationProvider>
          ) : (
            <CurrencyInput
              amount={amount}
              autoFocus={true}
              fullWidth={true}
              minAmount={0}
              setAmount={(value) => setAmount(Number(value) || 0)}
              sx={{ '.MuiTypography-body1': { p: 0 } }}
            />
          )}
        </Dialog>
      </Stack>
    )
  },
)
